import React, { useCallback, useContext } from 'react';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { AppContext, IAppContext } from 'appState/appContext';
import { convertUndefinedFields } from 'utils/helpers';
import { CreateUserContext, ICreateUserContext } from '../../createUserState/createUserContext';
import { createUser } from 'backend/users';
import classNames from 'classnames';
import style from './ModalWindows.module.scss';

const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		resetFields,
		showErrorModalWithInfo,
		changeTypeOfModalWindow,
		dataForSubmit,
		typeOfModalWindow,
		errorResponseData,
	} = useContext<ICreateUserContext>(CreateUserContext);
	const containerClasses = classNames(style.container, { [style.visible]: typeOfModalWindow || !!errorResponseData });

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await createUser(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.USER_EMAIL_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
					changeTypeOfModalWindow(null);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleResetFields = useCallback(() => {
		resetFields();
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
		changeTypeOfModalWindow(null);
	}, []);

	return (
		<div className={containerClasses} data-test="ModalWindows">
			<ModalToConfirm
				onConsentEvent={handleResetFields}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleResetFields}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'fileSizeExceeded'}
				description="fileSizeExceeded"
				translationOptions={{ size: '1MB' }}
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'fileUploadError'}
				description="fileUploadError"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'invalidFileFormat'}
				description="invalidFileFormatPhoto"
				translationOptions={{ formats: '.jpeg, .jpg, .png' }}
			/>
			{errorResponseData && (
				<ModalSentError
					onConsentEvent={handleCloseModalWithInfo}
					isVisible={true}
					description={errorResponseData.Type}
					translationOptions={{
						value: errorResponseData.Metadata.email,
					}}
				/>
			)}
		</div>
	);
};

export default ModalWindows;
